import React, { useState, useEffect } from 'react';

interface CustomerChatModalProps {
  partnerName: string;
  orderId: string;
  orderStatus: string;
  onClose: () => void;
}

const CustomerChatModal: React.FC<CustomerChatModalProps> = ({ 
  partnerName,
  orderId,
  orderStatus,
  onClose
}) => {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<Array<{sender: string, text: string, time: string}>>([]);
  const [lastMessageCount, setLastMessageCount] = useState(0);

  // Load chat history from API and poll for updates
  useEffect(() => {
    const loadChat = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`https://api.bringit.co.in/api/orders/${orderId}/chat`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          const formattedMessages = data.chatMessages.map((msg: any) => ({
            sender: msg.sender,
            text: msg.text,
            time: new Date(msg.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
          }));
          
          // Show notification if new partner message received
          if (formattedMessages.length > lastMessageCount && lastMessageCount > 0) {
            const latestMsg = formattedMessages[formattedMessages.length - 1];
            console.log('New message detected:', latestMsg);
            if (latestMsg.sender === 'Partner') {
              console.log('Partner message - showing notification');
              // Play notification sound
              const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSl+zPLTgjMGHm7A7+OZUQ8MW6vn7bdlHwU7k9n0y3gsBSh+y/DYiTYIGmy98dqZUhAJU6/p7qxYFQxGn+PwtWMcBz2T2PPQeysFKIHO89iKOAgZbL3y251SD');
              audio.play().catch(() => {});
              
              // Show browser notification
              if ('Notification' in window && Notification.permission === 'granted') {
                new Notification('New message from Delivery Partner', {
                  body: latestMsg.text,
                  icon: '🚴'
                });
              }
              // Show alert popup
              setTimeout(() => {
                alert(`💬 New message from ${partnerName}:\n${latestMsg.text}`);
              }, 100);
            }
          }
          
          setLastMessageCount(formattedMessages.length);
          setChatHistory(formattedMessages);
        }
      } catch (error) {
        console.error('Error loading chat:', error);
      }
    };

    loadChat();
    
    // Poll for new messages every 2 seconds
    const interval = setInterval(loadChat, 2000);
    
    return () => clearInterval(interval);
  }, [orderId]);

  const handleSendMessage = async () => {
    if (message.trim()) {
      try {
        const token = localStorage.getItem('token');
        await fetch(`https://api.bringit.co.in/api/orders/${orderId}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            sender: 'Customer',
            text: message
          })
        });
        setMessage('');
      } catch (error) {
        console.error('Error sending message:', error);
      }
    }
  };

  const quickMessages = [
    "Thank you!",
    "Please hurry",
    "Call me when you arrive",
    "Is everything available?",
    "How much longer?"
  ];

  const getStatusColor = (status: string) => {
    const statusMap: {[key: string]: string} = {
      'Pending': '#ffa500',
      'Confirmed': '#4ECDC4',
      'Partner En Route to Store': '#3498db',
      'At Store': '#9b59b6',
      'Items Packed': '#e67e22',
      'Out for Delivery': '#6366f1',
      'At Customer Location': '#27ae60',
      'Delivered': '#28a745'
    };
    return statusMap[status] || '#999';
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>💬 Chat with Partner</h3>
            <p style={styles.subtitle}>{partnerName}</p>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Order Status */}
        <div style={{...styles.statusBar, backgroundColor: getStatusColor(orderStatus)}}>
          <span style={styles.statusIcon}>📦</span>
          <span style={styles.statusText}>{orderStatus}</span>
        </div>

        {/* Order Info */}
        <div style={styles.orderInfo}>
          <span style={styles.orderLabel}>Order ID:</span>
          <span style={styles.orderId}>{orderId.substring(0, 8)}...</span>
        </div>

        {/* Chat History */}
        <div style={styles.chatContainer}>
          {chatHistory.map((msg, index) => {
            const isCustomer = msg.sender === 'Customer' || msg.sender === 'You';
            return (
              <div key={index} style={isCustomer ? styles.messageWrapperRight : styles.messageWrapperLeft}>
                <div style={isCustomer ? styles.messageYou : styles.messagePartner}>
                  <div style={styles.messageSender}>{isCustomer ? 'You' : 'Partner'}</div>
                  <div style={styles.messageText}>{msg.text}</div>
                  <div style={styles.messageTime}>{msg.time}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Replies */}
        <div style={styles.quickReplies}>
          <p style={styles.quickTitle}>Quick Replies:</p>
          <div style={styles.quickButtons}>
            {quickMessages.map((msg, index) => (
              <button
                key={index}
                style={styles.quickBtn}
                onClick={() => setMessage(msg)}
              >
                {msg}
              </button>
            ))}
          </div>
        </div>

        {/* Message Input */}
        <div style={styles.inputContainer}>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Type your message..."
            style={styles.input}
          />
          <button style={styles.sendBtn} onClick={handleSendMessage}>
            Send
          </button>
        </div>

        {/* Info */}
        <div style={styles.footer}>
          <p style={styles.note}>
            💡 Your delivery partner will respond soon
          </p>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(4px)',
    animation: 'fadeIn 0.2s ease-out',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    animation: 'slideUp 0.3s ease-out',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px',
    borderBottom: '1px solid #e0e0e0',
  },
  title: {
    margin: '0 0 5px 0',
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#333',
  },
  subtitle: {
    margin: 0,
    fontSize: '14px',
    color: '#666',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#999',
    padding: '0',
    width: '30px',
    height: '30px',
  },
  statusBar: {
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: 'white',
    fontWeight: 'bold',
  },
  statusIcon: {
    fontSize: '20px',
  },
  statusText: {
    fontSize: '14px',
  },
  orderInfo: {
    padding: '10px 20px',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    gap: '10px',
    fontSize: '14px',
  },
  orderLabel: {
    color: '#666',
    fontWeight: '600',
  },
  orderId: {
    color: '#333',
    fontFamily: 'monospace',
  },
  chatContainer: {
    flex: 1,
    padding: '20px',
    overflowY: 'auto',
    minHeight: '200px',
    maxHeight: '300px',
    backgroundColor: '#fafafa',
  },
  messageWrapperLeft: {
    marginBottom: '15px',
    display: 'flex',
    justifyContent: 'flex-start',
  },
  messageWrapperRight: {
    marginBottom: '15px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  messagePartner: {
    backgroundColor: '#fff',
    padding: '12px',
    borderRadius: '12px',
    maxWidth: '75%',
    border: '1px solid #e0e0e0',
  },
  messageYou: {
    backgroundColor: '#6366f1',
    color: 'white',
    padding: '12px',
    borderRadius: '12px',
    maxWidth: '75%',
  },
  messageSender: {
    fontSize: '12px',
    fontWeight: 'bold',
    marginBottom: '5px',
    opacity: 0.8,
  },
  messageText: {
    fontSize: '14px',
    marginBottom: '5px',
    lineHeight: '1.4',
  },
  messageTime: {
    fontSize: '11px',
    opacity: 0.7,
  },
  quickReplies: {
    padding: '15px 20px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
  },
  quickTitle: {
    margin: '0 0 10px 0',
    fontSize: '13px',
    fontWeight: '600',
    color: '#666',
  },
  quickButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  quickBtn: {
    padding: '6px 12px',
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '20px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#333',
    transition: 'all 0.2s',
  },
  inputContainer: {
    display: 'flex',
    gap: '10px',
    padding: '15px 20px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: 'white',
  },
  input: {
    flex: 1,
    padding: '12px 15px',
    border: '1px solid #e0e0e0',
    borderRadius: '25px',
    fontSize: '14px',
    outline: 'none',
  },
  sendBtn: {
    padding: '12px 25px',
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '25px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  footer: {
    padding: '12px 20px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fa',
  },
  note: {
    margin: 0,
    fontSize: '12px',
    color: '#666',
    textAlign: 'center',
  },
};

export default CustomerChatModal;
